import { useState } from "react";
import { Sparkles, Mail, Loader2, Copy, Check, User, Building, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { toast } from "sonner";

interface AIEmailTemplateGeneratorProps {
  lead?: {
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    email?: string | null;
    status?: string | null;
  };
  deal?: {
    title?: string;
    amount?: number | null;
    accounts?: { name: string } | null;
    contacts?: { first_name: string; last_name: string } | null;
  };
  onTemplateGenerated?: (subject: string, content: string) => void;
}

const emailTypes = [
  { id: "intro", label: "Introduction", description: "First contact with a prospect" },
  { id: "follow_up", label: "Follow-up", description: "Continue a previous conversation" },
  { id: "proposal", label: "Proposal", description: "Present an offer or quote" },
  { id: "check_in", label: "Check-in", description: "Touch base with existing contact" },
  { id: "thank_you", label: "Thank You", description: "Post-meeting appreciation" },
  { id: "re_engagement", label: "Re-engagement", description: "Reconnect with cold lead" },
];

const tones = [
  { id: "professional", label: "Professional" },
  { id: "friendly", label: "Friendly" },
  { id: "casual", label: "Casual" },
  { id: "formal", label: "Formal" },
  { id: "enthusiastic", label: "Enthusiastic" },
];

export function AIEmailTemplateGenerator({ lead, deal, onTemplateGenerated }: AIEmailTemplateGeneratorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [emailType, setEmailType] = useState("follow_up");
  const [tone, setTone] = useState("professional");
  const [additionalContext, setAdditionalContext] = useState("");
  const [generatedSubject, setGeneratedSubject] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [copied, setCopied] = useState(false);
  const { chat, isLoading } = useAIAssistant();

  const recipientName = lead 
    ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim() 
    : deal?.contacts 
      ? `${deal.contacts.first_name} ${deal.contacts.last_name}`
      : "";
  
  const companyName = lead?.company_name || deal?.accounts?.name || "";

  const handleGenerate = async () => {
    const typeLabel = emailTypes.find(t => t.id === emailType)?.label || emailType;
    const toneLabel = tones.find(t => t.id === tone)?.label || tone;

    const prompt = `Generate a ${toneLabel.toLowerCase()} ${typeLabel.toLowerCase()} email for a sales outreach.

Recipient Details:
- Name: ${recipientName || "Prospect"}
- Company: ${companyName || "their company"}
${lead?.status ? `- Lead Status: ${lead.status}` : ""}
${deal?.title ? `- Related Deal: ${deal.title}` : ""}
${deal?.amount ? `- Deal Value: $${deal.amount.toLocaleString()}` : ""}
${additionalContext ? `\nAdditional Context: ${additionalContext}` : ""}

Requirements:
1. Write a compelling subject line (max 60 chars)
2. Keep the email concise (under 150 words)
3. Include a clear call-to-action
4. Personalize based on the available information
5. Make it feel human and authentic

Format your response as:
SUBJECT: [your subject line]

[email body]`;

    const response = await chat(prompt);
    
    if (response) {
      // Parse subject and content
      const lines = response.split("\n");
      let subject = "";
      let content = "";
      let foundSubject = false;

      for (const line of lines) {
        if (line.toUpperCase().startsWith("SUBJECT:")) {
          subject = line.replace(/^SUBJECT:\s*/i, "").trim();
          foundSubject = true;
        } else if (foundSubject) {
          content += line + "\n";
        }
      }

      setGeneratedSubject(subject);
      setGeneratedContent(content.trim());
    }
  };

  const handleCopy = () => {
    const fullEmail = `Subject: ${generatedSubject}\n\n${generatedContent}`;
    navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    toast.success("Email copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUseTemplate = () => {
    onTemplateGenerated?.(generatedSubject, generatedContent);
    setIsOpen(false);
    toast.success("Template applied");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Wand2 className="h-4 w-4" />
          AI Email
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">AI Email Generator</p>
              <p className="text-sm text-muted-foreground font-normal">
                Create personalized emails with AI
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Recipient Info */}
          {(recipientName || companyName) && (
            <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
              {recipientName && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{recipientName}</span>
                </div>
              )}
              {companyName && (
                <div className="flex items-center gap-2 text-sm">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <span>{companyName}</span>
                </div>
              )}
            </div>
          )}

          {/* Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email Type</Label>
              <Select value={emailType} onValueChange={setEmailType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {emailTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div>
                        <p>{type.label}</p>
                        <p className="text-xs text-muted-foreground">{type.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tones.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Additional Context (optional)</Label>
            <Textarea
              placeholder="E.g., We discussed pricing on our last call. They're interested in the enterprise plan..."
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              className="resize-none"
              rows={2}
            />
          </div>

          <Button onClick={handleGenerate} disabled={isLoading} className="gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Generate Email
          </Button>

          {/* Generated Content */}
          {(generatedSubject || generatedContent) && (
            <div className="flex-1 overflow-hidden border border-border rounded-lg">
              <div className="p-3 border-b border-border bg-muted/30">
                <Label className="text-xs text-muted-foreground">Subject</Label>
                <p className="font-medium">{generatedSubject}</p>
              </div>
              <ScrollArea className="h-[200px]">
                <div className="p-3">
                  <Label className="text-xs text-muted-foreground">Body</Label>
                  <p className="whitespace-pre-wrap text-sm mt-1">{generatedContent}</p>
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        {/* Actions */}
        {generatedContent && (
          <div className="flex items-center gap-2 pt-4 border-t border-border">
            <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleGenerate} disabled={isLoading} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Regenerate
            </Button>
            <div className="flex-1" />
            {onTemplateGenerated && (
              <Button onClick={handleUseTemplate}>Use This Template</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
